import React, { useEffect, useState } from "react";
import KnowledgeSourceScope from "./KnowledgeSourceScope";
import WritingBriefPanel from "./WritingBriefPanel";

// Custom-instructions modal for regenerating a section. On submit it fires
// `onRegenerate({ instructions })` and closes immediately — the spinner shows
// in the editor, so the modal does not block on the request.
const RegenerateInstructionsModal = ({
  isOpen,
  onClose,
  generationPrompt = "",
  onRegenerate,
  orgId,
  folderId,
  isSubsection = false,
  initialKbScope = null,
  initialRfpScope = null,
}) => {
  const unit = isSubsection ? "Subsection" : "Section";
  const unitLower = isSubsection ? "subsection" : "section";
  const [instructions, setInstructions] = useState("");
  const [scope, setScope] = useState({
    kb_folder_ids: [],
    kb_file_keys: [],
    rfp_folder_ids: [],
    rfp_file_keys: [],
  });

  useEffect(() => {
    if (isOpen) setInstructions("");
  }, [isOpen]);

  if (!isOpen) return null;

  // Instructions are OPTIONAL: this dialog absorbed the old "Change Knowledge
  // Scope" action, so adjusting the sources alone is a valid regenerate. An
  // empty instruction makes the backend re-draft from the node's stored brief
  // against the new scope, rather than rewriting to an instruction.
  // A knowledge source is still required — unless nothing is selectable
  // anywhere, in which case we fall back to the whole KB.
  const hasSelection =
    scope.kb_folder_ids.length +
      scope.kb_file_keys.length +
      scope.rfp_folder_ids.length +
      scope.rfp_file_keys.length >
    0;
  const noSources = scope.sourcesReady && !scope.hasSources;
  const canSubmit = hasSelection || noSources;

  const handleSubmit = () => {
    if (!canSubmit) return;
    // Selecting the whole KB is equivalent to an empty KB scope (backend searches
    // the entire collection) — send empty so it stamps a "Complete Knowledge
    // Base" scope rather than a "Custom Scope". Project (RFP) scope is left as-is:
    // an empty RFP scope means "no project evidence", so a full selection there
    // must stay explicit.
    onRegenerate?.({
      instructions: instructions.trim(),
      kb_folder_ids: scope.kbAllSelected ? [] : scope.kb_folder_ids,
      kb_file_keys: scope.kbAllSelected ? [] : scope.kb_file_keys,
      rfp_folder_ids: scope.rfp_folder_ids,
      rfp_file_keys: scope.rfp_file_keys,
      // Distinguishes a whole-KB pick (empty scope, whole KB) from a KB-scoped-out
      // pick (empty scope, no KB) so the next regenerate restores the selection.
      kb_whole: !!scope.kbAllSelected,
    });
    onClose?.();
  };

  return (
     <div className="fixed inset-0 z-[9990] flex items-center justify-center p-4" >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[680px] flex flex-col max-h-[90vh]">
        <div className="px-6 pt-6 pb-3 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-[#18101C] font-outfit text-xl font-semibold">
                Regenerate {unit}
              </h2>
              <p className="text-[#6B7280] font-outfit text-sm mt-1">
                Adjust the knowledge sources, add instructions, or both — then
                rewrite this {unitLower}.
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              aria-label="Close"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Order runs from what you change to what you consult: sources, then
            your instruction, then the brief it all sits on top of. */}
        <div className="px-6 py-5 flex flex-col gap-5 overflow-y-auto">
          <KnowledgeSourceScope
            isOpen={isOpen}
            folderId={folderId}
            orgId={orgId}
            initialKbScope={initialKbScope}
            initialRfpScope={initialRfpScope}
            onChange={setScope}
            selectAllKbWhenEmpty
          />

          <div>
            <label className="flex items-baseline gap-2 text-[#171717] font-outfit font-medium text-[15px] mb-2">
              Instructions
              <span className="font-normal text-[13px] text-[#9CA3AF]">
                Optional — leave empty to rewrite from the brief below
              </span>
            </label>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={3}
              placeholder={`e.g., Lead with the implementation timeline and keep the ${unitLower} under 400 words.`}
              className="w-full p-3 bg-white border border-gray-200 rounded-lg resize-none font-outfit text-[14px] text-[#18101C] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#8B55DD] focus:border-transparent"
              autoFocus
            />
          </div>

          {/* What this content was written from — read-only prose, not a field. */}
          <WritingBriefPanel
            label="Writing Brief"
            text={generationPrompt}
            tooltip="The brief this content was generated from. Your instructions above are applied on top of it."
          />
        </div>

        <div className="px-6 py-4 flex items-center justify-center gap-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-lg border border-[#8B55DD] text-[#8B55DD] font-outfit font-semibold text-[15px] hover:bg-[#8B55DD0A] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-6 py-2.5 rounded-lg bg-[#8B55DD] text-white font-outfit font-semibold text-[15px] hover:bg-[#7a45cc] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Regenerate {unit}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegenerateInstructionsModal;
